import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import toast from 'react-hot-toast';
import Tooltip from '@mui/material/Tooltip';
import { IconHelp } from '@tabler/icons-react';
import Typography from '@mui/material/Typography';

import { FormInput } from '@components/FormInput/ui/FormInput';
import { Button } from '@shared/components';
import { Toast } from '@components/Toast/ui/Toast';
import useDataFetcher from '@hooks/useDataFetcher/useDataFetcher';
import { urls } from '@api/urls.ts';
import { Prompt, PromptCategory } from '@app/types/core/prompt';
import useMutation from '@hooks/useMutation/useMutation';

import styles from './AIPromptsForm.module.scss';

interface AiSettingsFormValues {
  [key: string]: string;
}

function AIPromptsForm() {
  const [promptCategories, setPromptCategories] = useState<PromptCategory[]>(
    []
  );

  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [validationSchema, setValidationSchema] =
    useState<Yup.AnyObjectSchema>();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<AiSettingsFormValues>({
    resolver: validationSchema ? yupResolver(validationSchema) : undefined,
    mode: 'onChange',
  });

  const { data: categories } = useDataFetcher<PromptCategory[]>({
    url: urls.promptCategory.base,
  });

  const { trigger: updatePrompts } = useMutation({
    url: urls.prompt.updateMultiple,
    method: 'patch',
    onSuccess: () => {
      toast(({ id }) => (
        <Toast
          id={id}
          type="success"
          title="Success"
          text="Prompts have been updated successfully"
        />
      ));
    },
    onError: () => {
      toast(({ id }) => (
        <Toast
          id={id}
          type="error"
          title="Error"
          text="Failed to update prompts"
        />
      ));
    },
  });

  const onSubmit = (data: AiSettingsFormValues) => {
    const updatedPrompts = prompts.map((prompt) => ({
      id: prompt.id,
      prompt: data[`prompt_${prompt.id}`],
    }));

    updatePrompts(updatedPrompts);
  };

  useEffect(() => {
    if (!categories) return;

    setPromptCategories(categories);
    const filteredPrompts = categories.flatMap((category) => category.prompts);
    setPrompts(filteredPrompts);

    const defaultValues: AiSettingsFormValues = {};
    const schemaShape: { [key: string]: Yup.StringSchema } = {};

    filteredPrompts.forEach((prompt: Prompt) => {
      const fieldName = `prompt_${prompt.id}`;
      defaultValues[fieldName] = prompt.prompt;

      schemaShape[fieldName] = Yup.string()
        .required('This field is required')
        .min(3, 'Must be at least 3 characters');
    });

    reset(defaultValues);

    const schema = Yup.object().shape(schemaShape);
    setValidationSchema(schema);
  }, [categories, setPrompts, setPromptCategories, reset]);

  return (
    <form className={styles.formWrapper} onSubmit={handleSubmit(onSubmit)}>
      {prompts.length === 0 ? (
        <div className={styles.loadingBox}>
          <p>Loading...</p>
        </div>
      ) : (
        promptCategories.map((category) => (
          <div key={category.key}>
            <div className={styles.formSection}>
              <Typography variant="h5">{category.name}</Typography>
              {category.description && (
                <Tooltip
                  title={category.description}
                  arrow
                  className={styles.tooltip}
                  color="gray"
                >
                  <IconHelp size={14} />
                </Tooltip>
              )}
            </div>

            <div className={styles.line} />

            <div className={styles.row}>
              {category.prompts.map((prompt: Prompt) => {
                const fieldName = `prompt_${prompt.id}`;
                return (
                  <div key={prompt.id}>
                    <FormInput
                      bordered
                      type="textarea"
                      required
                      labelText={prompt.name}
                      placeholder={prompt.description}
                      {...register(fieldName)}
                      errorText={errors[fieldName]?.message}
                      inputClassname={styles.resize}
                      tooltipText={prompt.description}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      <div className={styles.line} />

      <div className={styles.actions}>
        <div className={styles.wrap}>
          <Button
            type="submit"
            variant="active"
            disabled={!isValid || prompts.length === 0}
          >
            Save
          </Button>
        </div>
      </div>
    </form>
  );
}

export default AIPromptsForm;
